/**
 * Pure tier-gating decisions for the sidebar's Pro-only nav items
 * (ADN-2278/2280, Task Group 7). Extracted out of `SidebarComponent` so
 * they're testable without Angular's TestBed — this repo's Jest config runs
 * under `testEnvironment: 'node'` with no `jest-preset-angular` (see
 * agent-os/standards/testing/qa-test-plan-prompt.md's "Known test-harness
 * limitations"), and `SidebarComponent` field-injects its services via
 * `inject()`, which throws outside an Angular injection context — the same
 * reason `membership-tier-utils.ts` and `membership-tier-guard-logic.ts`
 * exist as standalone functions rather than living on their respective
 * classes.
 */

/**
 * Lock affordance for a Pro-only nav item on a Core-tier org. The item
 * stays visible and clickable either way (spec.md FR-3.3) — this only
 * drives the visual lock icon/class; the actual gate is
 * `MembershipTierGuard` on the target route.
 *
 * Gated on `hasFetched` — `AuthService` fires `fetchStatus()` without
 * awaiting it, so right after login `isPro` can still hold its pre-fetch
 * Core default. Without this guard, a Pro org would flash a lock icon on
 * every Pro-only item until the fetch resolves. Showing no lock during
 * that brief unknown-tier window is the safer default than showing a
 * wrong one.
 */
export function isNavItemLocked(item: { requireTier?: 'pro' }, hasFetched: boolean, isPro: boolean): boolean {
  return item.requireTier === 'pro' && hasFetched && !isPro;
}

/**
 * Label for the Billing nav item (ADN-2282/2283, TG8/TG9) — spec.md
 * FR-3.3/UI Requirements: "Billing entry switches label/route by tier."
 * Before the tier fetch resolves, `isPro` still holds its pre-fetch Core
 * default, so this defaults to the Core-side label — a brief incorrect
 * label on a clickable-either-way nav item is a much smaller cost than the
 * lock-icon flicker `isNavItemLocked` guards against (that one could
 * misrepresent a paying org as blocked; this one just risks one stale
 * click before the fetch resolves, and the destination page's own
 * `ngOnInit` re-checks tier and redirects correctly regardless).
 */
export function billingNavLabel(isPro: boolean): string {
  return isPro ? 'Manage Subscription' : 'Upgrade to Pro';
}

/** Route for the Billing nav item — see `billingNavLabel` for the framing. */
export function billingNavRoute(isPro: boolean): string {
  return isPro ? '/billing' : '/billing/upgrade';
}
